import { CopyOutlined } from '@ant-design/icons';
import { Button, Card, Col, Empty, Row, Spin, Switch, Tag, Typography, Modal, Table, Tabs, Radio, Statistic } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { formatDateOnly } from '../../helpers/date';
import moment from 'moment';
import useAxiosPost from '../../hooks/useAxiosPost';
import useMultiNotification from '../../hooks/useNotification';
import '../Items/Cards/site/singleSiteCard.css';
import '../Items/Table/index.css';
import OnlineStatusIndicator from '../Items/Status/OnlineStatusIndicator';
import { getUserType } from '../../helpers/userDetails';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface RouterSingleRowWithCopyProps extends SingleRowRouterDetailsProp {
  copyValue?: string;
  onCopy?: (value: string, label: string) => void;
}

const RouterSingleRow: React.FC<RouterSingleRowWithCopyProps> = ({
  title,
  value,
  valueSpecialClass,
  copyValue,
  onCopy,
}) => {
  const handleCopy = () => {
    if (copyValue && onCopy) {
      onCopy(copyValue, title);
    }
  };

  return (
    <Row gutter={[6, 16]}>
      <Col span={6}>
        <div className="router-details-label-container">
          <Typography className="router-details-label">{title}</Typography>
          <Typography className="router-details-label">:</Typography>
        </div>
      </Col>
      <Col span={18}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: 'space-between',
          }}
        >
          <Typography className={`router-details-value ${valueSpecialClass || ''}`} style={{ flex: 1 }}>
            {value || '-'}
          </Typography>
          {copyValue && (
            <Button
              type="text"
              icon={<CopyOutlined />}
              size="small"
              onClick={handleCopy}
              style={{
                color: '#3A77BC',
                padding: '4px 8px',
                minWidth: 'auto',
              }}
              title={`Copy ${title}`}
            />
          )}
        </div>
      </Col>
    </Row>
  );
};

interface ConnectedUsersChartProps {
  statsData: any;
  period: 'daily' | 'yesterday' | 'weekly' | 'monthly';
}

const ConnectedUsersChart: React.FC<ConnectedUsersChartProps> = ({ statsData, period }) => {
  const formatDateForDisplay = (dateStr: string, isHour: boolean = false): string => {
    if (!dateStr) return '';
    if (isHour) {
      // Format hour: "2024-01-15 14:00:00" -> "14:00"
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } else {
      // Format date: "2024-01-15" -> "15 Jan"
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const day = date.getDate();
      const month = moment(date).format('MMM');
      return `${day} ${month}`;
    }
  };

  // Guard against invalid data
  if (!statsData || !statsData.stats || !Array.isArray(statsData.stats) || statsData.stats.length === 0) {
    return <Empty description="No data available for the chart" />;
  }

  // Ensure period matches the data structure
  if (statsData.period && statsData.period !== period) {
    return <Empty description="Loading data for selected period..." />;
  }

  let categories: string[] = [];
  let seriesData: any[] = [];
  let chartOptions: ApexOptions;

  if (period === 'daily' || period === 'yesterday') {
    // Hourly data
    const validStats = statsData.stats.filter(
      (item: any) => item && item.hour && item.connectedUsers !== null && item.connectedUsers !== undefined,
    );

    if (validStats.length === 0) {
      return <Empty description="No valid data available for the chart" />;
    }

    categories = validStats.map((item: any) => formatDateForDisplay(item.hour, true));
    seriesData = [
      {
        name: 'Connected Users',
        data: validStats.map((item: any) => {
          const value = Number(item.connectedUsers);
          return isNaN(value) ? 0 : value;
        }),
      },
    ];

    chartOptions = {
      chart: {
        type: 'line',
        height: 350,
        toolbar: {
          show: false,
        },
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          colors: ['#304758'],
        },
      },
      xaxis: {
        categories: categories,
        title: {
          text: 'Time',
          style: {
            fontSize: '14px',
            fontWeight: 600,
          },
        },
      },
      yaxis: {
        title: {
          text: 'Connected Users',
          style: {
            fontSize: '14px',
            fontWeight: 600,
          },
        },
        labels: {
          formatter: (value: number) => {
            return Math.floor(value).toString();
          },
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.25,
          gradientToColors: ['#3A77BC'],
          inverseColors: false,
          opacityFrom: 0.85,
          opacityTo: 0.85,
          stops: [0, 100],
        },
      },
      colors: ['#3A77BC'],
      tooltip: {
        y: {
          formatter: (value: number) => {
            return `${value} user${value !== 1 ? 's' : ''}`;
          },
        },
      },
      grid: {
        borderColor: '#e0e0e0',
        strokeDashArray: 3,
      },
    };
  } else {
    // Weekly/Monthly - Daily data with min/max
    const validStats = statsData.stats.filter(
      (item: any) =>
        item &&
        item.date &&
        item.minConnectedUsers !== null &&
        item.minConnectedUsers !== undefined &&
        item.maxConnectedUsers !== null &&
        item.maxConnectedUsers !== undefined,
    );

    if (validStats.length === 0) {
      return <Empty description="No valid data available for the chart" />;
    }

    categories = validStats.map((item: any) => formatDateForDisplay(item.date, false));
    seriesData = [
      {
        name: 'Minimum Connected Users',
        data: validStats.map((item: any) => {
          const value = Number(item.minConnectedUsers);
          return isNaN(value) ? 0 : value;
        }),
        color: '#ff4d4f',
      },
      {
        name: 'Maximum Connected Users',
        data: validStats.map((item: any) => {
          const value = Number(item.maxConnectedUsers);
          return isNaN(value) ? 0 : value;
        }),
        color: '#52c41a',
      },
    ];

    chartOptions = {
      chart: {
        type: 'bar',
        height: 350,
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: false,
          columnWidth: '55%',
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -20,
        style: {
          fontSize: '12px',
          colors: ['#304758'],
        },
      },
      xaxis: {
        categories: categories,
        title: {
          text: 'Date',
          style: {
            fontSize: '14px',
            fontWeight: 600,
          },
        },
      },
      yaxis: {
        title: {
          text: 'Connected Users',
          style: {
            fontSize: '14px',
            fontWeight: 600,
          },
        },
        labels: {
          formatter: (value: number) => {
            return Math.floor(value).toString();
          },
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.25,
          gradientToColors: ['#3A77BC'],
          inverseColors: false,
          opacityFrom: 0.85,
          opacityTo: 0.85,
          stops: [0, 100],
        },
      },
      colors: ['#ff4d4f', '#52c41a'],
      tooltip: {
        y: {
          formatter: (value: number) => {
            return `${value} user${value !== 1 ? 's' : ''}`;
          },
        },
      },
      grid: {
        borderColor: '#e0e0e0',
        strokeDashArray: 3,
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
      },
    };
  }

  // Ensure we have valid data before rendering
  if (
    !categories ||
    categories.length === 0 ||
    !seriesData ||
    seriesData.length === 0 ||
    !seriesData[0] ||
    !seriesData[0].data ||
    seriesData[0].data.length === 0
  ) {
    return <Empty description="No data available for the chart" />;
  }

  // Ensure categories and data arrays have the same length
  const dataLength = seriesData[0].data.length;
  if (categories.length !== dataLength) {
    console.warn('Categories and data length mismatch', { categories: categories.length, data: dataLength });
    return <Empty description="Data format error: categories and data length mismatch" />;
  }

  // Ensure all series have the same data length
  const allSeriesHaveSameLength = seriesData.every((series) => series.data && series.data.length === dataLength);
  if (!allSeriesHaveSameLength) {
    console.warn('Series data length mismatch');
    return <Empty description="Data format error: series data length mismatch" />;
  }

  return (
    <ReactApexChart
      options={chartOptions}
      series={seriesData}
      type={period === 'daily' || period === 'yesterday' ? 'line' : 'bar'}
      height={350}
    />
  );
};

const RouterDetailsComponent = () => {
  const { intPkSiteCoreRouterID } = useParams();
  const { openNotification, contextHolder } = useMultiNotification();
  const postApiDataRef = useRef<any>(null);

  const [postApiData, apiResponse, apiError, apiLoading, apiMessage, apiErrorCount] =
    useAxiosPost<ResponseProp>('routers/details');

  const [
    postToggleStatusData,
    toggleStatusResponse,
    toggleStatusError,
    toggleStatusLoading,
    toggleStatusMessage,
    toggleStatusErrorCount,
  ] = useAxiosPost<ResponseProp>('routers/toggle-status');

  const [
    postOnlineUsersApiData,
    onlineUsersApiResponse,
    onlineUsersApiError,
    onlineUsersApiLoading,
    onlineUsersApiMessage,
    onlineUsersApiErrorCount,
  ] = useAxiosPost<ResponseProp>('routers/online-users');

  const [postStatsApiData, statsApiResponse, statsApiError, statsApiLoading, statsApiMessage, statsApiErrorCount] =
    useAxiosPost<ResponseProp>('routers/connected-users-stats');

  const handleCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      openNotification('success', 'Copied!', `${label} copied to clipboard`);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = value;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        openNotification('success', 'Copied!', `${label} copied to clipboard`);
      } catch (error) {
        openNotification('error', 'Failed to Copy', 'Could not copy to clipboard');
      }
      document.body.removeChild(textArea);
    }
  };

  const [routerDetails, setRouterDetails] = useState<any>(null);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUserTableDataType[]>([]);
  const [isToggleModalOpen, setIsToggleModalOpen] = useState(false);
  const [pendingEnableValue, setPendingEnableValue] = useState<number | null>(null);
  const [statsPeriod, setStatsPeriod] = useState<'daily' | 'yesterday' | 'weekly' | 'monthly'>('daily');
  const [statsData, setStatsData] = useState<any>(null);
  const hasProcessedToggleSuccess = useRef(false);
  const lastProcessedToggleErrorCount = useRef(0);

  useEffect(() => {
    postApiDataRef.current = postApiData;
  }, [postApiData]);

  useEffect(() => {
    if (intPkSiteCoreRouterID) {
      postApiData({ id: Number(intPkSiteCoreRouterID) } as any);
      postOnlineUsersApiData({ id: Number(intPkSiteCoreRouterID) } as any);
      postStatsApiData({ id: Number(intPkSiteCoreRouterID), period: statsPeriod } as any);
    }
  }, [intPkSiteCoreRouterID]);

  useEffect(() => {
    if (intPkSiteCoreRouterID && statsPeriod) {
      postStatsApiData({ id: Number(intPkSiteCoreRouterID), period: statsPeriod } as any);
    }
  }, [statsPeriod, intPkSiteCoreRouterID]);

  useEffect(() => {
    if (apiResponse !== null) {
      const responseData: any = apiResponse.responseData;
      setRouterDetails(responseData);
    }
  }, [apiResponse]);

  useEffect(() => {
    if (onlineUsersApiResponse !== null) {
      const responseData: any = onlineUsersApiResponse.responseData;
      if (Array.isArray(responseData)) {
        setOnlineUsers(responseData);
      }
    }
  }, [onlineUsersApiResponse]);

  useEffect(() => {
    if (statsApiResponse !== null) {
      const responseData: any = statsApiResponse.responseData;
      setStatsData(responseData);
    }
  }, [statsApiResponse]);

  useEffect(() => {
    if (toggleStatusResponse !== null && !hasProcessedToggleSuccess.current) {
      hasProcessedToggleSuccess.current = true;
      const backendMessage =
        (toggleStatusResponse as any).message ||
        `Router ${pendingEnableValue === 1 ? 'enabled' : 'disabled'} successfully`;
      openNotification('success', 'Status Updated', backendMessage);
      setIsToggleModalOpen(false);
      setPendingEnableValue(null);
      // Refresh router details
      if (intPkSiteCoreRouterID) {
        postApiDataRef.current({ id: Number(intPkSiteCoreRouterID) } as any);
      }
    }
  }, [toggleStatusResponse, intPkSiteCoreRouterID, pendingEnableValue, openNotification]);

  // Reset the flag when toggleStatusResponse becomes null
  useEffect(() => {
    if (toggleStatusResponse === null) {
      hasProcessedToggleSuccess.current = false;
    }
  }, [toggleStatusResponse]);

  useEffect(() => {
    if (
      toggleStatusError !== null &&
      toggleStatusErrorCount !== 0 &&
      lastProcessedToggleErrorCount.current !== toggleStatusErrorCount
    ) {
      lastProcessedToggleErrorCount.current = toggleStatusErrorCount;
      openNotification('error', 'Failed to Update Status', toggleStatusError);
      setIsToggleModalOpen(false);
      setPendingEnableValue(null);
    }
  }, [toggleStatusError, toggleStatusErrorCount, openNotification]);

  const handleStatusToggle = (checked: boolean) => {
    const newEnableValue = checked ? 1 : 0;
    setPendingEnableValue(newEnableValue);
    setIsToggleModalOpen(true);
  };

  const handleConfirmToggle = () => {
    if (pendingEnableValue !== null && intPkSiteCoreRouterID) {
      postToggleStatusData({
        id: Number(intPkSiteCoreRouterID),
        enable: pendingEnableValue,
      } as any);
    }
  };

  const handleCancelToggle = () => {
    setIsToggleModalOpen(false);
    setPendingEnableValue(null);
  };

  if (apiLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <Typography style={{ marginTop: '16px', display: 'block' }}>Loading router details...</Typography>
      </div>
    );
  }

  if (apiError && !apiLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Empty
          description={<Typography.Text type="danger">{apiError || 'Failed to load router details'}</Typography.Text>}
        />
      </div>
    );
  }

  if (!routerDetails) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Empty description="Router details not found" />
      </div>
    );
  }

  const { routerName, coreRouterIp, enable, dateCreated, core_router_vendor, company_site } = routerDetails;

  const vendorName = core_router_vendor?.coreRouterVendorName || '-';
  const siteName = company_site?.siteName || '-';

  const onlineUsersColumns: ColumnsType<OnlineUserTableDataType> = [
    {
      title: 'Voucher Number',
      dataIndex: 'voucherNumber',
      key: 'voucherNumber',
      render: (text: string) => text || '-',
    },
    {
      title: 'MAC Address',
      dataIndex: 'eUserDevMAC',
      key: 'eUserDevMAC',
      render: (text: string) => text || '-',
    },
    {
      title: 'User IP',
      dataIndex: 'eUserDevIP',
      key: 'eUserDevIP',
      render: (text: string) => text || '-',
    },
    {
      title: 'Total Upload',
      dataIndex: 'formattedTotalUploadedBytes',
      key: 'formattedTotalUploadedBytes',
      render: (text: string) => text || '0 B',
    },
    {
      title: 'Total Download',
      dataIndex: 'formattedTotalDownloadedBytes',
      key: 'formattedTotalDownloadedBytes',
      render: (text: string) => text || '0 B',
    },
    {
      title: 'Connected Since',
      dataIndex: 'formattedConnectedSince',
      key: 'formattedConnectedSince',
      render: (text: string) => text || '0 seconds',
    },
  ];

  return (
    <>
      {contextHolder}
      <Row gutter={[16, 16]}>
        {/* Header Card */}
        <Col span={24}>
          <Card className="border-card">
            <div className="site-card-top-items">
              <div className="site-details-box">
                <div className="site-title-box">
                  <Typography className="site-header-title">{routerName || 'Router Details'}</Typography>
                </div>
                <div>
                  <Typography className="site-header-address">{siteName}</Typography>
                </div>
              </div>
              <div className="site-status-indicator-box">
                <OnlineStatusIndicator isOnline={Boolean(enable)} />
                <span style={{ marginRight: '12px' }}>{enable ? 'ENABLED' : 'DISABLED'}</span>
                <Tag color={enable ? 'success' : 'default'}>{enable ? 'Active' : 'Disabled'}</Tag>
              </div>
            </div>
          </Card>
        </Col>

        {/* Online Users Count Card - Prominent for Admin */}
        {getUserType() === 'ADMIN' && (
          <Col span={24}>
            <Card
              className="border-card"
              style={{
                background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
                border: '2px solid #1890ff',
                marginBottom: '16px',
                boxShadow: '0 4px 12px rgba(24, 144, 255, 0.2)',
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <Row gutter={16} align="middle">
                <Col flex="auto">
                  <Typography.Text
                    style={{
                      color: '#000000',
                      fontSize: '16px',
                      fontWeight: 500,
                      display: 'block',
                      marginBottom: '8px',
                    }}
                  >
                    Online Users
                  </Typography.Text>
                  <Statistic
                    value={onlineUsers.length}
                    valueStyle={{ 
                      color: '#000000', 
                      fontSize: '42px', 
                      fontWeight: 700,
                    }}
                    suffix={
                      <span style={{ 
                        color: '#000000', 
                        fontSize: '20px', 
                        fontWeight: 500,
                      }}>
                        {onlineUsers.length === 1 ? 'User' : 'Users'}
                      </span>
                    }
                  />
                </Col>
                <Col>
                  <div
                    style={{
                      width: '90px',
                      height: '90px',
                      borderRadius: '50%',
                      background: '#1890ff',
                      border: '3px solid #096dd9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(24, 144, 255, 0.4)',
                    }}
                  >
                    <Typography.Text
                      style={{
                        color: '#ffffff',
                        fontSize: '36px',
                        fontWeight: 700,
                      }}
                    >
                      {onlineUsers.length}
                    </Typography.Text>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        )}

        {/* Basic Information Card */}
        <Col span={24}>
          <Card className="border-card" title="Router Information">
            <RouterSingleRow title="Router Name" value={routerName} copyValue={routerName} onCopy={handleCopy} />
            <RouterSingleRow title="Local IP" value={coreRouterIp} copyValue={coreRouterIp} onCopy={handleCopy} />
            <RouterSingleRow title="Vendor" value={vendorName} />
            <RouterSingleRow title="Site Name" value={siteName} />
            <RouterSingleRow title="Date Created" value={dateCreated ? formatDateOnly(dateCreated) : '-'} />
            <Row gutter={[6, 16]}>
              <Col span={6}>
                <div className="router-details-label-container">
                  <Typography className="router-details-label">Status</Typography>
                  <Typography className="router-details-label">:</Typography>
                </div>
              </Col>
              {getUserType() === 'ADMIN' && (
                <Col span={18}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <Switch
                      checked={Boolean(enable)}
                      onChange={handleStatusToggle}
                      disabled={toggleStatusLoading}
                      checkedChildren="Enabled"
                      unCheckedChildren="Disabled"
                    />
                    <Tag color={enable ? 'success' : 'default'}>{enable ? 'Active' : 'Disabled'}</Tag>
                  </div>
                </Col>
              )}
            </Row>
          </Card>
        </Col>

        {/* Tabs for Online Users and Statistics */}
        <Col span={24}>
          <Card className="border-card">
            <Tabs
              defaultActiveKey="online-users"
              items={[
                {
                  key: 'online-users',
                  label: 'Online Users',
                  children: (
                    <>
                      {onlineUsersApiLoading ? (
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                          <Spin size="large" />
                          <Typography style={{ marginTop: '16px', display: 'block' }}>
                            Loading online users...
                          </Typography>
                        </div>
                      ) : onlineUsersApiError && !onlineUsersApiLoading ? (
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                          <Empty
                            description={
                              <Typography.Text type="danger">
                                {onlineUsersApiError || 'Failed to load online users'}
                              </Typography.Text>
                            }
                          />
                        </div>
                      ) : onlineUsers.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                          <Empty description="No online users found for this router." />
                        </div>
                      ) : (
                        <div className="table-container-custom">
                          <Table
                            className="antd-table-striped"
                            columns={onlineUsersColumns}
                            dataSource={onlineUsers}
                            pagination={false}
                            loading={onlineUsersApiLoading}
                            rowKey={(record) => `${record.acctSessionID}-${record.eUserDevMAC}`}
                          />
                        </div>
                      )}
                    </>
                  ),
                },
                {
                  key: 'statistics',
                  label: 'Statistics',
                  children: (
                    <div>
                      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                        <Radio.Group
                          value={statsPeriod}
                          onChange={(e) => setStatsPeriod(e.target.value)}
                          buttonStyle="solid"
                        >
                          <Radio.Button value="daily">Daily</Radio.Button>
                          <Radio.Button value="yesterday">Yesterday</Radio.Button>
                          <Radio.Button value="weekly">Weekly</Radio.Button>
                          <Radio.Button value="monthly">Monthly</Radio.Button>
                        </Radio.Group>
                      </div>

                      {statsApiLoading ? (
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                          <Spin size="large" />
                          <Typography style={{ marginTop: '16px', display: 'block' }}>Loading statistics...</Typography>
                        </div>
                      ) : statsApiError ? (
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                          <Empty
                            description={
                              <Typography.Text type="danger">
                                {statsApiError || 'Failed to load statistics'}
                              </Typography.Text>
                            }
                          />
                        </div>
                      ) : !statsData || !statsData.stats || statsData.stats.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                          <Empty description="No statistics data available for the selected period." />
                        </div>
                      ) : (
                        <div>
                          {/* Min/Max Labels for Daily and Yesterday */}
                          {(statsPeriod === 'daily' || statsPeriod === 'yesterday') &&
                            statsData.minConnectedUsers !== null &&
                            statsData.maxConnectedUsers !== null && (
                              <div
                                style={{ marginBottom: '16px', display: 'flex', gap: '24px', justifyContent: 'center' }}
                              >
                                <Typography.Text strong style={{ color: '#52c41a' }}>
                                  Maximum: {statsData.maxConnectedUsers} users
                                </Typography.Text>
                                <Typography.Text strong style={{ color: '#ff4d4f' }}>
                                  Minimum: {statsData.minConnectedUsers} users
                                </Typography.Text>
                              </div>
                            )}
                          <ConnectedUsersChart statsData={statsData} period={statsPeriod} />
                        </div>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Toggle Status Confirmation Modal */}
      <Modal
        title={
          <span>
            <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: '8px' }} />
            Confirm Status Change
          </span>
        }
        open={isToggleModalOpen}
        onOk={handleConfirmToggle}
        onCancel={handleCancelToggle}
        confirmLoading={toggleStatusLoading}
        okText="Confirm"
        cancelText="Cancel"
        okButtonProps={{
          danger: pendingEnableValue === 0,
        }}
      >
        <Typography.Paragraph>
          {pendingEnableValue === 1
            ? 'Are you sure you want to enable this router? This will allow the router to process connections.'
            : 'Are you sure you want to disable this router? This will prevent the router from processing new connections. Existing connections may be affected.'}
        </Typography.Paragraph>
      </Modal>
    </>
  );
};

export default RouterDetailsComponent;
